import {
  useDeleteWorkspaceMutation,
  useGetWorkspaceMembersQuery,
  useLeaveWorkspaceMutation,
  useUpdateWorkspaceMutation,
} from '@carboncopyco/data';
import { notify } from '@carboncopyco/ui';
import { useWorkspaceAnalytics } from '@carboncopyco/utils';
import {
  WorkspaceLeaveModal,
  workspaceModalLabels,
} from '../WorkspaceLeaveModal';
import { WorkspaceManageTagModal } from '../WorkspaceManageTagModal';
import { WorkspaceMembersModalWrapper } from '../WorkspaceMembersModal';
import { WorkspaceSettingsModal } from '../WorkspaceSettingsModal';
import { WorkspaceArchiveModal } from '../WorkspacesArchiveModal';
import { useWorkspacesPage } from './WorkspacesPageContext';

export const WorkspacesPageModals = () => {
  const {
    organizationId,
    selectedWorkspace,
    isSettingsModalOpen,
    setOpenMembersModal,
    isMembersModalOpen,
    setOpenArchiveModal,
    isArchiveModalOpen,
    setOpenLeaveModal,
    isLeaveModalOpen,
    setOpenSettingsModal,
    archiveModalLabel,
    isManageTagsOpen,
    setOpenManageTags,
    setSelectedWorkspace,
    removeItem,
    replaceItem,
  } = useWorkspacesPage();

  const {
    trackWorkspaceClickCloseMembersModal,
    trackWorkspaceClickLeave,
    trackWorkspaceClickArchiveDeactivateSharedLinks,
    trackWorkspaceClickApproveArchive,
    trackWorkspaceClickCancelArchive,
  } = useWorkspaceAnalytics('all_workspaces_page');

  const [leaveWorkspace] = useLeaveWorkspaceMutation();
  const [archiveWorkspace] = useDeleteWorkspaceMutation();
  const workspaceId = selectedWorkspace?.id;
  const { refetch: refetchMembers } = useGetWorkspaceMembersQuery(
    { organizationId, workspaceId },
    { skip: !workspaceId, refetchOnFocus: true }
  );
  const [updateWorkspace, { isLoading: isUpdatingWorkspace }] =
    useUpdateWorkspaceMutation();

  const handleArchiveAnalyticsEvents = (mode) => {
    if (mode === workspaceModalLabels.leaveLastMember) {
      return trackWorkspaceClickArchiveDeactivateSharedLinks({
        workspace: selectedWorkspace,
      });
    }
    trackWorkspaceClickApproveArchive();
  };

  if (!selectedWorkspace) {
    return null;
  }

  return (
    <>
      <WorkspaceSettingsModal
        isOpen={isSettingsModalOpen}
        handleSubmit={(settings) => {
          const { workspaceType, ...rest } = settings;
          const workspaceSettings =
            workspaceType === 'default' ? rest : settings;

          updateWorkspace({
            organizationId,
            id: workspaceId,
            ...workspaceSettings,
          })
            .unwrap()
            .then(() => {
              replaceItem(selectedWorkspace.id, {
                ...selectedWorkspace,
                ...workspaceSettings,
              });
              notify.success(
                'Workspace settings have been updated successfully'
              );
              setOpenSettingsModal(false);
              setSelectedWorkspace(null);
            })
            .catch(() => notify.error('Unable to update workspace settings'));
        }}
        handleClose={() => {
          setOpenSettingsModal(false);
          setSelectedWorkspace(null);
        }}
        isLoading={isUpdatingWorkspace}
        settings={{
          workspaceType: selectedWorkspace?.workspaceType,
          workspaceName: selectedWorkspace?.workspaceName,
          color: selectedWorkspace?.color,
        }}
      />

      <WorkspaceMembersModalWrapper
        onRemove={() => {
          replaceItem(selectedWorkspace.id, {
            ...selectedWorkspace,
            totalMembers: selectedWorkspace.totalMembers - 1,
          });
        }}
        onInvite={(emails) => {
          replaceItem(selectedWorkspace.id, {
            ...selectedWorkspace,
            totalMembers: selectedWorkspace.totalMembers + emails?.length,
          });
        }}
        workspace={selectedWorkspace}
        show={isMembersModalOpen}
        onClose={() => {
          setSelectedWorkspace(null);
          refetchMembers();
          setOpenMembersModal(false);
          trackWorkspaceClickCloseMembersModal();
        }}
      />

      <WorkspaceArchiveModal
        show={isArchiveModalOpen}
        label={archiveModalLabel}
        onClose={() => {
          setSelectedWorkspace(null);
          trackWorkspaceClickCancelArchive({
            workspace: selectedWorkspace,
          });
          setOpenArchiveModal(false);
        }}
        archiveWorkspace={() => {
          removeItem(String(selectedWorkspace?.id));
          archiveWorkspace({
            organizationId,
            id: workspaceId,
          })
            .unwrap()
            .then(() => {
              notify.success(
                'Workspace has been archived successfully and its shared links have been deactivated'
              );
              handleArchiveAnalyticsEvents(archiveModalLabel);
            })
            .catch(() => {
              notify.error('There was an error archiving this workspace');
            })
            .finally(() => setOpenArchiveModal(false));
        }}
      />
      <WorkspaceLeaveModal
        show={isLeaveModalOpen}
        onClose={() => {
          setSelectedWorkspace(null);
          setOpenLeaveModal(false);
        }}
        leaveWorkspace={() =>
          leaveWorkspace({ organizationId, workspaceId })
            .unwrap()
            .then(() => {
              replaceItem(workspaceId, {
                ...selectedWorkspace,
                isMember: false,
                totalMembers: selectedWorkspace.totalMembers - 1,
              });
              notify.success(
                `You have left ${selectedWorkspace?.workspaceName} workspace`
              );
              trackWorkspaceClickLeave();
            })
            .catch(() =>
              notify.error('There was an error leaving this workspace')
            )
            .finally(() => {
              setSelectedWorkspace(null);
              setOpenLeaveModal(false);
            })
        }
      />
      <WorkspaceManageTagModal
        isOpen={isManageTagsOpen}
        organizationId={organizationId}
        workspace={selectedWorkspace}
        onClose={(tags) => {
          if (tags) {
            replaceItem(selectedWorkspace?.id, {
              ...selectedWorkspace,
              tags,
            });
          }
          setSelectedWorkspace(null);
          setOpenManageTags(false);
        }}
      />
    </>
  );
};
